import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Inbox, AlertTriangle, CheckSquare } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const toneStyles = {
  amber: 'border-amber-200 bg-amber-50 text-amber-700',
  red: 'border-red-200 bg-red-50 text-red-700',
  orange: 'border-orange-200 bg-orange-50 text-orange-700',
  blue: 'border-blue-200 bg-blue-50 text-blue-700',
};

export default function AdminSubRoleWork({ userName }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWork = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/my-work`, {
          headers: { },
        });
        if (!res.ok) throw new Error('Failed to load your work queue');
        setData(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchWork();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-gray-600">Loading your work queue…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="subrole-work-dashboard">
      {/* Hero */}
      <div className="rounded-2xl bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(193_80%_45%)] p-8 text-white">
        <p className="text-white/80 text-sm">Welcome back{userName ? `, ${userName.split(' ')[0]}` : ''}</p>
        <h1 className="text-3xl font-bold mt-1" data-testid="subrole-work-title">{data.title}</h1>
        <p className="text-white/90 mt-2 max-w-xl">{data.subtitle}</p>
        {data.cta && (
          <Button
            className="mt-5 bg-white text-[hsl(var(--primary))] hover:bg-gray-100 font-semibold"
            onClick={() => navigate(data.cta.path)}
            data-testid="subrole-work-cta"
          >
            {data.cta.label} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>

      {/* Count cards */}
      {data.cards?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.cards.map((card, i) => (
            <button
              key={i}
              onClick={() => navigate(card.path)}
              className={`text-left rounded-xl border-2 p-6 transition-transform hover:-translate-y-0.5 ${toneStyles[card.tone] || toneStyles.blue}`}
              data-testid={`subrole-work-card-${i}`}
            >
              <p className="text-sm font-medium opacity-80">{card.label}</p>
              <p className="text-4xl font-bold mt-2">{card.count}</p>
              <span className="inline-flex items-center text-xs font-semibold mt-3">
                View <ArrowRight className="w-3 h-3 ml-1" />
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Items list */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Needs your attention</h2>
        {(!data.items || data.items.length === 0) ? (
          <Card>
            <CardContent className="p-8 text-center">
              <CheckSquare className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <p className="text-gray-600">You're all caught up — nothing pending right now.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {data.items.map((item, i) => (
              <Card key={i} className="hover:shadow-md transition-shadow" data-testid={`subrole-work-item-${i}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-[hsl(var(--primary))]/10 flex items-center justify-center shrink-0">
                    <Inbox className="w-5 h-5 text-[hsl(var(--primary))]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-900 truncate">{item.title}</p>
                    <p className="text-sm text-gray-600 truncate">{item.subtitle}</p>
                  </div>
                  {item.meta && <span className="text-xs text-gray-400 shrink-0">{item.meta}</span>}
                </CardContent>
              </Card>
            ))}
            {data.cta && (
              <Button variant="outline" className="w-full" onClick={() => navigate(data.cta.path)} data-testid="subrole-work-viewall">
                Go to {data.cta.label}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
